import React, {memo, useEffect} from 'react'
import {Features} from '../shared'
import {startCase} from 'lodash'
import {Button, Card, Grid, Header, Icon, List, Message, Modal, Segment} from 'semantic-ui-react'
import ConfirmPlan from './confirm-plan'
import {useSafeState} from '../../hook'
import {withPropsPresent} from '../../hoc'
import Billing from './billing'

const _SelectPlan = (
  {
    account,
    subscription,
    onSubscriptionUpdated
  }
) => {
  const [state, setState] = useSafeState({error: '', display: 'view', newPlanName: '', subscription})

  useEffect(() => {
    setState({...state, subscription})
  }, [subscription])

  const currentPlanName = state.subscription.planName

  const onPlanSelected = async (e, newPlanName) => {
    e.preventDefault()
    if (newPlanName === 'free') {
      if (account.stats.userCount > account.limits.maxUserCount) {
        return setState({
          ...state,
          error: `You cannot switch to free plan until you reduce your user count to: ${account.limits.maxUserCount}`
        })
      }
      if (account.stats.serviceCount > account.limits.maxServiceCount) {
        return setState({
          ...state,
          error: `You cannot switch to free plan until you reduce your service count to: ${account.limits.maxServiceCount}`
        })
      }
    } else {
      if (!state.subscription.paymentMethod) {
        return setState({...state, error: 'You need to add a card prior to selecting this plan'})
      }
    }
    setState({...state, error: '', display: 'confirm', newPlanName})
  }

  const onClose = (updatedSubscription) => {
    setState({...state, display: 'view', newPlanName: '', subscription: updatedSubscription || state.subscription})
    onSubscriptionUpdated(updatedSubscription)
  }

  if (state.error) {
    return (
      <Modal open={true} closeOnDimmerClick={true} closeIcon={true} onClose={() => setState({...state, error: ''})}>
        <Modal.Header content='Plan Change Error'/>
        <Modal.Content>
          <Message error data-testid='plan-change-error'>{state.error}</Message>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setState({...state, error: ''})}>OK</Button>
        </Modal.Actions>
      </Modal>
    )
  }

  if (state.display === 'lets-talk') {
    return (
      <Modal open={true} closeOnDimmerClick={true} closeIcon={true}
             onClose={() => setState({...state, display: 'view'})} data-testid='custom-plan-modal'>
        <Modal.Header content='Need A Custom Plan?'/>
        <Modal.Content>
          <Segment>
            <Header as='h3'>We're here for you!</Header>
            <p>Please send an email to <u>jeremy@servicemocks.com</u> with a subject of <i><u>Need Custom Plan</u></i></p>
            <p>We'll talk with through and figure out what makes sense for your needs.</p>
          </Segment>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setState({...state, display: 'view'})}>OK</Button>
        </Modal.Actions>
      </Modal>
    )
  }

  const customerId = state.subscription.customerId
  const seats = state.subscription.seats

  const onCustomPlanSelected = !!account.customPlanSettings.name ? (e) => onPlanSelected(e) : () => setState({
    ...state,
    display: 'lets-talk'
  })

  const confirmationTimestamp = new Date()

  return (
    <>
      {
        state.display === 'confirm' && (
          <ConfirmPlan {...{
            plan: state.newPlanName,
            subscription: state.subscription,
            customerId,
            seats,
            confirmationTimestamp,
            onClose
          }}/>)
      }
      <Header as='h3'>Your Plan</Header>
      <Grid columns='16' data-testid='select-plan'>
        <Grid.Row>
          <Grid.Column width='5' data-testid='free-plan-card'>
            <Card raised={currentPlanName === 'free'}>
              <Card.Content>
                <Card.Header>{currentPlanName === 'free' && <Icon name='check' size='large'/>} Free</Card.Header>
              </Card.Content>
              <Card.Content>
                <Header as='h3'>$0.00/User/Month</Header>
              </Card.Content>
              <Card.Content>
                <Header
                  as='h4'>Limits{`${currentPlanName === 'free' && account.limits.customized ? ' (custom)' : ''}`}</Header>
                <List bulleted>
                  <List.Item>
                    <Features linkText='All Features'/>
                  </List.Item>
                  <List.Item>
                    {
                      currentPlanName === 'free' && account.limits.customized ? `${account.limits.maxUserCount} User(s)` : '5 User(s)'
                    }
                  </List.Item>
                  <List.Item>
                    {
                      currentPlanName === 'free' && account.limits.customized ? `${account.limits.maxServiceCount} Service Mock(s)` : '2 Service Mocks(s)'
                    }
                  </List.Item>
                </List>
              </Card.Content>
              <Card.Content>
                <Button primary
                        basic
                        loading={state.newPlanName === 'free' && state.display === 'confirm'}
                        disabled={currentPlanName === 'free'}
                        data-testid='free-plan-select-button'
                        onClick={(e) => onPlanSelected(e, 'free')}>{currentPlanName === 'free' ? 'Selected' : 'Select'}</Button>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column width='5' data-testid='monthly-plan-card'>
            <Card raised={currentPlanName === 'monthly'}>
              <Card.Content>
                <Card.Header>{currentPlanName === 'monthly' && <Icon name='check' size='large'/>} Monthly</Card.Header>
              </Card.Content>
              <Card.Content>
                <Header as='h3'>$7.99/User/Month</Header>
              </Card.Content>
              <Card.Content>
                <Header
                  as='h4'>Limits{`${currentPlanName === 'monthly' && account.limits.customized ? ' (custom)' : ''}`}</Header>
                <List bulleted>
                  <List.Item>
                    <Features linkText='All Features'/>
                  </List.Item>
                  <List.Item>
                    Unlimited User(s)
                  </List.Item>
                  <List.Item>
                    Unlimited Service Mock(s)
                  </List.Item>
                </List>
              </Card.Content>
              <Card.Content>
                <Button primary
                        basic
                        loading={state.newPlanName === 'monthly' && state.display === 'confirm'}
                        disabled={currentPlanName === 'monthly'}
                        data-testid='monthly-plan-select-button'
                        onClick={(e) => onPlanSelected(e, 'monthly')}>{currentPlanName === 'monthly' ? 'Selected' : 'Select'}</Button>
              </Card.Content>
            </Card>
          </Grid.Column>
          <Grid.Column width='5' data-testid='custom-plan-card'>
            <Card raised={currentPlanName === 'custom'}>
              <Card.Content>
                <Card.Header>
                  {currentPlanName === 'custom' && <Icon name='check' size='large'/>} Custom
                </Card.Header>
              </Card.Content>
              <Card.Content>
                {
                  currentPlanName === 'custom' && (
                    <Header
                      as='h3'>{state.subscription.items.data[0].plan.amount_decimal}/{startCase(state.subscription.items.data[0].plan.interval)}
                    </Header>
                  )
                }
                {
                  currentPlanName !== 'custom' && (<Header as='h3'>Flexible Pricing</Header>)
                }
              </Card.Content>
              <Card.Content>
                <Header
                  as='h4'>Limits</Header>
                <List bulleted>
                  <List.Item>
                    <Features linkText='All Features'/>
                  </List.Item>
                  <List.Item>
                    {
                      currentPlanName === 'custom' && `${account.limits.maxUserCount} User(s)`
                    }
                    {
                      currentPlanName !== 'custom' && 'Flexible User Limit'
                    }
                  </List.Item>
                  <List.Item>
                    {
                      currentPlanName === 'custom' && `${account.limits.maxUserCount} Service Mocks(s)`
                    }
                    {
                      currentPlanName !== 'custom' && 'Flexible Service Mock Limit'
                    }
                  </List.Item>
                </List>
              </Card.Content>
              <Card.Content>
                <Button primary
                        basic
                        loading={['custom', account.customPlanSettings.name].includes(state.newPlanName) && state.display === 'confirm'}
                        disabled={['custom', account.customPlanSettings.name].includes(currentPlanName)}
                        data-testid='custom-plan-select-button'
                        onClick={onCustomPlanSelected}>{currentPlanName === 'custom' ? 'Selected' : `Let's Talk`}</Button>
              </Card.Content>
            </Card>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  )
}

let SelectPlan = (props) => {
  return (<_SelectPlan account={props.account} subscription={props.subscription}
                       onSubscriptionUpdated={props.onSubscriptionUpdated}/>)
}

SelectPlan = withPropsPresent(SelectPlan, ['subscription'])
SelectPlan = memo(SelectPlan)

export {SelectPlan, _SelectPlan}

export default SelectPlan